import { z } from "zod";

import {
  managementRoleSchema,
  organizationRoleSchema,
} from "@/state/api/roles.types";

/**
 * auth-service's users on the wire (DEDUPE-2 §3.3), one schema for both
 * apps, ported from admin's parsers: the roles a user holds come with the
 * role itself when the backend joins it.
 */

const optionalNumber = z
  .number()
  .nullish()
  .transform((value) => value ?? undefined);
const optionalText = z
  .string()
  .nullish()
  .transform((value) => value ?? undefined);

export const userRoleSchema = z.object({
  id: optionalNumber,
  userId: optionalNumber,
  roleId: z.number(),
  objectId: optionalText,
  role: organizationRoleSchema.optional(),
  version: optionalNumber,
  createdAt: optionalText,
  updatedAt: optionalText,
});
export type UserRole = z.output<typeof userRoleSchema>;

export const userManagementRoleSchema = z.object({
  id: optionalNumber,
  userId: optionalNumber,
  managementRoleId: z.number(),
  organizationId: z.number().nullish(),
  managementRole: managementRoleSchema.optional(),
  version: optionalNumber,
  createdAt: optionalText,
  updatedAt: optionalText,
});
export type UserManagementRole = z.output<typeof userManagementRoleSchema>;

const relation = <TSchema extends z.ZodTypeAny>(item: TSchema) =>
  z
    .array(item)
    .nullish()
    .transform((value): z.output<TSchema>[] => value ?? []);

/** A user (auth-service `User`, `users.entity.ts`); `displayName` falls back to the email. */
export const authUserSchema = z
  .object({
    id: optionalNumber,
    email: z.string(),
    displayName: z.string().nullish(),
    uid: optionalText,
    photoURL: optionalText,
    aliases: z.array(z.string()).optional(),
    version: optionalNumber,
    createdAt: optionalText,
    updatedAt: optionalText,
    deletedAt: z.string().nullish(),
    userRoles: relation(userRoleSchema),
    userManagementRoles: relation(userManagementRoleSchema),
  })
  .transform(({ displayName, ...user }) => ({
    ...user,
    displayName: displayName ?? user.email,
  }));
export type AuthUser = z.output<typeof authUserSchema>;
